import React, { ReactNode } from 'react';
import { useFormikContext } from 'formik';
import {
  Chip,
  Box,
  FormControlLabel,
  TextField,
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
} from '@mui/material';

export const styles = {
  errorText: {
    color: '#E53935',
    fontSize: '11px',
    marginLeft: '10px',
  },
  labelRoot: {
    marginLeft: 0,
    marginRight: 0,
    width: '100%',
  },
  label: {
    display: 'flex',
  },
  labelPlacementTop: {
    alignItems: 'flex-start',
  },
  textInput: {
    width: '100%',
    border: 0,
    '& .MuiFilledInput-input': {
      width: '100%',
    },
  },
};

const textFieldStyles = {
  display: 'flex',
  flexDirection: 'column',
  '& .MuiFormHelperText-root': {
    mt: 0,
    ml: 0,
  },
  '& .MuiInputBase-root': {
    border: 'none',
    padding: 0,
    display: 'flex',
    color: 'black',
    height: '2.8em',
  },
  '& .MuiFormControl-root': {
    color: 'black',
    marginTop: 0,
  },
  '& .MuiFilledInput-root': {
    padding: 0,
  },
  '& .MuiFilledInput-input': {
    padding: 0,
  },
  '& .MuiFilledInput-input: hover': {
    padding: 0,
    backgroundColor: 'transparent',
  },
};

export interface DropdownOption {
  label: string;
  value: string;
}

/**
 * Function to extract the property from an object based on a property access string.
 */
export const getValueFromFieldArrayByString = (object: Record<string, unknown>, string: string): unknown => {
  let str = string.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
  str = str.replace(/^\./, ''); // strip a leading dot
  const a = str.split('.');
  let obj: unknown = object;
  for (let i = 0, n = a.length; i < n; ++i) {
    const k = a[i];
    if (obj && typeof obj === 'object' && k in obj) {
      obj = (obj as Record<string, unknown>)[k];
    } else {
      return;
    }
  }
  return obj;
};

interface FormikContextLike {
  values: Record<string, unknown>;
  errors: Record<string, unknown>;
  touched: Record<string, unknown>;
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
  setFieldTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void;
}

interface ValidatedDropdownProps {
  label: string;
  name: string;
  options: DropdownOption[];
  id?: string;
  isMultiChoice?: boolean;
  isDisabled?: boolean;
  loading?: boolean;
  action?: (result: DropdownOption | DropdownOption[] | null) => void;
  textFieldLabel?: string;
  propFormikContext?: FormikContextLike;
  restTextfieldProps?: Record<string, unknown>;
  labelComponent?: ReactNode;
}

/**
 * A dropdown field component.
 * @param label The displayed label (helper text) of the field.
 * @param name The name to be used as the field name in the Formik form.
 * @param options The possible options for the dropdown.
 * @param isMultiChoice Set whether multiple choices are allowed.
 * @param isDisabled Set whether the field is disabled.
 * @param id A custom ID given for the dropdown.
 * @param loading Indicates whether the field values are being loaded.
 * @param action Action to be executed on value change, if not default.
 * @param textFieldLabel Label to be displayed on the text field.
 * @param propFormikContext Formik context to be used when the dropdown is in a different context.
 */

export const ValidatedDropdown: React.FC<
  ValidatedDropdownProps & Partial<AutocompleteProps<DropdownOption, boolean, boolean, boolean>>
> = ({
  label,
  name,
  options,
  id,
  isMultiChoice = false,
  isDisabled = false,
  loading,
  action,
  textFieldLabel,
  propFormikContext,
  restTextfieldProps,
  labelComponent,
  ...rest
}) => {
  const fc = useFormikContext<Record<string, unknown>>();
  const formikContext = (propFormikContext ?? fc) as FormikContextLike;
  const formikValues = formikContext?.values ?? {};
  const itemValue = name?.includes('[') ? getValueFromFieldArrayByString(formikValues, name) : formikValues[`${name}`];

  return (
    <Autocomplete
      key={name}
      options={options ? options : []}
      data-testid={id ? `${id}-autocomplete` : `${name}-autocomplete`}
      id={`${name}-autocomplete`}
      disabled={isDisabled}
      disableCloseOnSelect={isMultiChoice}
      loading={loading}
      size="small"
      getOptionLabel={(option) => {
        return typeof option === 'string' ? option : option.label;
      }}
      value={
        loading || !options
          ? null
          : isMultiChoice
            ? (options?.filter((option) => itemValue && Array.isArray(itemValue) && itemValue.includes(option.value)) ??
              null)
            : (options.find((option) => option.value === itemValue) ?? null)
      }
      onChange={(_, value) => {
        if (action) {
          action(value as DropdownOption | DropdownOption[] | null);
        } else {
          if (isMultiChoice && Array.isArray(value)) {
            formikContext.setFieldValue(
              name,
              value.map((v) => (typeof v === 'string' ? v : v.value)),
            );
          } else if (value && typeof value !== 'string' && !Array.isArray(value)) {
            formikContext.setFieldValue(name, value.value);
          } else {
            formikContext.setFieldValue(name, value);
          }
          formikContext.setFieldTouched(name, true, false);
        }
      }}
      sx={textFieldStyles}
      onBlur={() => fc.setFieldTouched(name, true)}
      multiple={isMultiChoice}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              variant="outlined"
              label={option.label}
              id={`${name}-chip-${index}`}
              size="small"
              style={{ padding: 0, margin: 0 }}
              {...tagProps}
            />
          );
        })
      }
      renderInput={(params: AutocompleteRenderInputParams) => {
        return (
          <FormControlLabel
            label={
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'flex-start',
                }}
              >
                {label} {labelComponent}
              </Box>
            }
            id={`${name}-input-label`}
            labelPlacement="top"
            sx={{
              margin: 0,
              dispaly: 'flex',
              '& .MuiFormControlLabel-label': {
                width: '100%',
              },
            }}
            control={
              <TextField
                {...params}
                label={textFieldLabel}
                sx={textFieldStyles}
                error={!!(formikContext?.errors && Object.prototype.hasOwnProperty.call(formikContext.errors, name))}
                helperText={
                  formikContext?.touched &&
                  Object.prototype.hasOwnProperty.call(formikContext.touched, name) &&
                  (formikContext?.errors[`${name}`] as React.ReactNode)
                }
                fullWidth={true}
                inputProps={{
                  ...params.inputProps,
                  'data-testid': `${name}-content-input`,
                  id: `${name}-content-input`,
                }}
                variant="filled"
                margin="dense"
                InputProps={{ ...params.InputProps, disableUnderline: true }}
                {...restTextfieldProps}
              />
            }
          />
        );
      }}
      {...rest}
    />
  );
};
